import type { TFunction } from 'i18next';
import { parseSupplyRefusal } from '@/lib/trpc-error-utils';
import type { Product, SupplyAliasInfo } from '../types';

/**
 * Phase 92 (D-13, UI-SPEC Screen 4 R3): the operator sentence for a stock
 * write refused on a supply alias (a client product built on the 3PL's blank).
 *
 * - Client caller (the row belongs to the active org): names the 3PL only.
 *   Never another client's name (T-92-11-02). Without the 3PL's name, "your
 *   3PL" (BLU-1400): the client cannot change the blank, so never the CSV
 *   reason that says to.
 * - 3PL caller (the row belongs to a client): names the client and points at
 *   the blank to change instead.
 * - No SKU code, or a 3PL caller without the names: the CSV row reason, which
 *   names nobody.
 */
export function aliasStockWriteMessage(
  t: TFunction<'inventory'>,
  args: {
    sku: string | null | undefined;
    supplyAlias: SupplyAliasInfo | null | undefined;
    isClientCaller: boolean;
  },
): string {
  const { sku, supplyAlias, isClientCaller } = args;
  if (sku && isClientCaller) {
    return supplyAlias?.ownerName
      ? t('sharedSkus.supply.stockWriteClient', { sku, owner: supplyAlias.ownerName })
      : t('sharedSkus.supply.stockWriteClientNoOwner', { sku });
  }
  if (sku && supplyAlias?.clientName && supplyAlias.blankSku) {
    return t('sharedSkus.supply.stockWriteOwner', {
      sku,
      client: supplyAlias.clientName,
      blank: supplyAlias.blankSku,
    });
  }
  return t('sharedSkus.supply.csvRowReason');
}

/** The server's row code for a CSV or stocktake row refused because it is a supply alias. */
export const SUPPLY_ALIAS_REASON_CODE = 'supply_alias';

/**
 * The reason to show for an import row: the translated R3 CSV row reason for
 * a supply alias refusal, else the server's `reason` unchanged.
 */
export function importRowReason(
  t: TFunction<'inventory'>,
  row: { reason: string; reasonCode?: string | null },
): string {
  return row.reasonCode === SUPPLY_ALIAS_REASON_CODE ? t('sharedSkus.supply.csvRowReason') : row.reason;
}

/**
 * True when the active org owns the row (the client looking at its own alias).
 * A row with no org id is the caller's own row (single-tenant payloads omit it).
 */
export function isOwnRow(
  rowOrganizationId: string | null | undefined,
  activeOrganizationId: string | null | undefined,
): boolean {
  return !rowOrganizationId || !activeOrganizationId || rowOrganizationId === activeOrganizationId;
}

/**
 * Phase 92 (D-13, UI-SPEC Screen 4 R4 single): the sentence for a refused
 * pre-print of a supply SKU. Falls back to a sentence without the 3PL's name
 * when the name is not known (the cause that carries it is dropped on the
 * wire for now).
 */
export function preprintRefusedMessage(
  t: TFunction<'inventory'>,
  args: { sku: string; ownerName: string | null | undefined },
): string {
  return args.ownerName
    ? t('sharedSkus.supply.preprintSingle', { sku: args.sku, owner: args.ownerName })
    : t('sharedSkus.supply.preprintSingleNoOwner', { sku: args.sku });
}

/**
 * BLU-1400: the sentence for a catalog change the server refused because the
 * SKU is a supply alias (`SUPPLY_ALIAS_READ_ONLY`): the 3PL's garment. Only
 * the client's own edits reach this refusal, so the no-name version says
 * "your 3PL". Never another client's name. With no SKU code (none sent), the
 * dialog's sentence that names nothing.
 */
export function aliasReadOnlyMessage(
  t: TFunction<'inventory'>,
  args: { sku: string | null | undefined; ownerName: string | null | undefined },
): string {
  const { sku, ownerName } = args;
  if (!sku) return t('supplyAliasDialog.noteNoOwner');
  return ownerName
    ? t('sharedSkus.supply.readOnly', { sku, owner: ownerName })
    : t('sharedSkus.supply.readOnlyNoOwner', { sku });
}

/** What a refusal sentence reads off the SKU row the surface has in hand. */
export type SupplyRefusalRow = Pick<Product, 'sku' | 'ownerOrganizationId' | 'supplyAlias'>;

/**
 * Phase 92 / BLU-1400: the readable sentence for a supply alias refusal (a
 * stock or location write, or a catalog change), or null for any other error,
 * so the caller keeps its own message. Never the raw marker.
 *
 * Names come from `row`, the SKU row in hand. The caller is the alias's
 * client when the row is the active org's own (`isOwnRow`, the rule the
 * Inventory rows use); an own-catalog surface (Edit SKU) passes its SKU with
 * no org on it. With no row the caller's side is unknown, so a stock or
 * location refusal names nobody. A catalog change only ever comes from the
 * client, so it names the row's 3PL, else "your 3PL".
 */
export function supplyAliasRefusalText(
  t: TFunction<'inventory'>,
  err: unknown,
  { row, activeOrganizationId }: { row?: SupplyRefusalRow | null; activeOrganizationId?: string | null } = {},
): string | null {
  const refusal = parseSupplyRefusal(err);
  if (!refusal || (refusal.kind !== 'alias_stock_write' && refusal.kind !== 'alias_read_only')) return null;
  const sku = refusal.sku ?? row?.sku ?? null;
  if (refusal.kind === 'alias_read_only') {
    return aliasReadOnlyMessage(t, { sku, ownerName: row?.supplyAlias?.ownerName });
  }
  return aliasStockWriteMessage(t, {
    sku,
    supplyAlias: row?.supplyAlias,
    isClientCaller: row != null && isOwnRow(row.ownerOrganizationId, activeOrganizationId),
  });
}
